import {
  AUDIENCE_TYPES,
  FIELD_LIMITS,
  SERVICE_INTERESTS,
} from "@growthwise/core";
import { useRef, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { PageHero } from "../../components/PageHero";
import { ScrollReveal } from "../../components/ScrollReveal";
import { Seo } from "../../components/Seo";
import { Alert } from "../../components/ui";
import {
  ConsentCheckbox,
  HoneypotField,
  SelectField,
  SubmitButton,
  TextAreaField,
  TextField,
} from "../../components/forms";
import { useErrorMessage, useFieldErrors } from "../../hooks/useErrorMessage";
import { useLanguage } from "../../i18n/LanguageProvider";
import { api, newClientRequestId } from "../../services/api-client";

/**
 * Public contact form.
 *
 * Open to anonymous visitors, so it carries the full submission discipline
 * the brief asks for: client-side validation mirroring the server schema, a
 * stable idempotency key so a double-click or a retried request cannot
 * create two records, a honeypot, and explicit pending, success and failure
 * states.
 */
export const ContactPage = () => {
  const { t, language, path } = useLanguage();
  const toMessage = useErrorMessage();
  const toFieldErrors = useFieldErrors();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [organization, setOrganization] = useState("");
  const [serviceInterest, setServiceInterest] = useState<string>("");
  const [audienceType, setAudienceType] = useState<string>("");
  const [message, setMessage] = useState("");
  // Consent starts false and there is no code path that sets it true
  // except the user clicking the control.
  const [consent, setConsent] = useState(false);
  const [honeypot, setHoneypot] = useState("");

  const [pending, setPending] = useState(false);
  const [succeeded, setSucceeded] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  /**
   * One idempotency key per filled-in form.
   *
   * Created once and reused across retries, so a failed-then-retried submit
   * resolves to the same record. Regenerated only after a success, when the
   * next message is genuinely a new one.
   */
  const requestIdRef = useRef(newClientRequestId());

  const validate = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (name.trim().length === 0)
      errors["name"] = t("errors.validation_failed");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      errors["email"] = t("errors.auth.invalid_email");
    }
    if (serviceInterest === "")
      errors["serviceInterest"] = t("errors.validation_failed");
    if (message.trim().length < FIELD_LIMITS.messageMin) {
      errors["message"] = t("errors.validation_failed");
    }
    if (!consent)
      errors["consentToContact"] = t("errors.consent_to_contact_required");
    return errors;
  };

  const handleSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (pending) return;

    const clientErrors = validate();
    if (Object.keys(clientErrors).length > 0) {
      setFieldErrors(clientErrors);
      setFormError(t("common.requiredFields"));
      return;
    }

    setPending(true);
    setFormError(null);
    setFieldErrors({});

    try {
      await api.submitContactForm({
        name: name.trim(),
        email: email.trim().toLowerCase(),
        serviceInterest,
        audienceType: audienceType === "" ? null : audienceType,
        organization: organization.trim() === "" ? null : organization.trim(),
        message: message.trim(),
        locale: language,
        consentToContact: true,
        clientRequestId: requestIdRef.current,
        antiAbuseToken: null,
        website: honeypot,
      });

      setSucceeded(true);
    } catch (error: unknown) {
      setFormError(toMessage(error));
      setFieldErrors(toFieldErrors(error));
    } finally {
      setPending(false);
    }
  };

  const resetForm = (): void => {
    setName("");
    setEmail("");
    setOrganization("");
    setServiceInterest("");
    setAudienceType("");
    setMessage("");
    setConsent(false);
    setHoneypot("");
    setSucceeded(false);
    setFormError(null);
    setFieldErrors({});
    requestIdRef.current = newClientRequestId();
  };

  return (
    <>
      <Seo
        titleKey="meta.contact.title"
        descriptionKey="meta.contact.description"
      />

      <PageHero
        variant="contact"
        eyebrow={t("contact.eyebrow")}
        title={t("contact.title")}
        subtitle={t("contact.subtitle")}
      />

      <section className="gw-section">
        <div className="gw-container">
          <div
            className="gw-grid gw-grid--2"
            style={{ alignItems: "start", gap: "var(--gw-space-7)" }}
          >
            <ScrollReveal direction="left">
              <h2 className="gw-title">{t("contact.formHeading")}</h2>
              <p className="gw-note">{t("contact.formIntro")}</p>

              {succeeded ? (
                <div style={{ marginTop: "1.5rem" }}>
                  <Alert tone="success" title={t("contact.successHeading")}>
                    {t("contact.successBody")}
                  </Alert>
                  <div className="gw-form-actions">
                    <button
                      type="button"
                      className="gw-btn gw-btn--secondary"
                      onClick={resetForm}
                    >
                      {t("contact.sendAnother")}
                    </button>
                  </div>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  noValidate
                  style={{ marginTop: "1.5rem" }}
                >
                  {formError ? (
                    <div style={{ marginBottom: "1.25rem" }}>
                      <Alert tone="danger">{formError}</Alert>
                    </div>
                  ) : null}

                  <TextField
                    label={t("form.name")}
                    required
                    name="name"
                    autoComplete="name"
                    maxLength={FIELD_LIMITS.nameMax}
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    error={fieldErrors["name"]}
                  />

                  <TextField
                    label={t("form.email")}
                    required
                    name="email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    maxLength={FIELD_LIMITS.emailMax}
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    error={fieldErrors["email"]}
                  />

                  <SelectField
                    label={t("form.interest")}
                    required
                    name="serviceInterest"
                    placeholder={t("form.selectPlaceholder")}
                    value={serviceInterest}
                    onChange={(event) => setServiceInterest(event.target.value)}
                    error={fieldErrors["serviceInterest"]}
                    options={SERVICE_INTERESTS.map((value) => ({
                      value,
                      label: t(`form.interests.${value}`),
                    }))}
                  />

                  <SelectField
                    label={t("form.audience")}
                    name="audienceType"
                    placeholder={t("form.selectPlaceholder")}
                    value={audienceType}
                    onChange={(event) => setAudienceType(event.target.value)}
                    error={fieldErrors["audienceType"]}
                    options={AUDIENCE_TYPES.map((value) => ({
                      value,
                      label: t(`form.audiences.${value}`),
                    }))}
                  />

                  <TextField
                    label={t("form.organizationOptional")}
                    name="organization"
                    autoComplete="organization"
                    maxLength={FIELD_LIMITS.organizationMax}
                    value={organization}
                    onChange={(event) => setOrganization(event.target.value)}
                    error={fieldErrors["organization"]}
                  />

                  <TextAreaField
                    label={t("form.message")}
                    required
                    name="message"
                    hint={t("form.messageHint")}
                    maxLength={FIELD_LIMITS.messageMax}
                    rows={6}
                    value={message}
                    onChange={(event) => setMessage(event.target.value)}
                    error={fieldErrors["message"]}
                  />

                  <ConsentCheckbox
                    name="consentToContact"
                    checked={consent}
                    onChange={setConsent}
                    error={fieldErrors["consentToContact"]}
                  >
                    {t("form.consentContact")}{" "}
                    <Link to={path("/privacy")}>{t("footer.privacy")}</Link>
                  </ConsentCheckbox>

                  <HoneypotField value={honeypot} onChange={setHoneypot} />

                  <div className="gw-form-actions">
                    <SubmitButton
                      pending={pending}
                      label={t("form.submit")}
                      pendingLabel={t("form.submitting")}
                    />
                  </div>
                </form>
              )}
            </ScrollReveal>

            <ScrollReveal direction="right" delay={100}>
              <div className="gw-stack">
                <div
                  className="gw-card"
                  style={{
                    background: "var(--gw-sky-100)",
                    borderColor: "transparent",
                  }}
                >
                  <p className="gw-eyebrow">{t("contact.checkInPrompt")}</p>
                  <p style={{ marginTop: "0.75rem" }}>
                    <Link
                      to={path("/check-in")}
                      className="gw-btn gw-btn--primary gw-btn--sm"
                    >
                      {t("home.assessmentLink")}
                    </Link>
                  </p>
                </div>

                <Alert tone="info" title={t("footer.crisisHeading")}>
                  {t("footer.crisisBody")}
                </Alert>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>
    </>
  );
};
